import React from 'react';
import { Button, Tooltip } from '@myua/kit';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import * as actions from '../actions';
import {
  getUploadToProjectEnabled,
  getCanUpload,
  getCompletedInBackground,
  getUploadStats,
} from '../selectors';
import { UploadModal } from './UploadModal';

const UploadIcon16 = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      opacity="0.4"
      d="M4.80011 6.5H11.1995C12.1608 6.5 12.8906 6.79877 13.3782 7.28711C13.8657 7.77529 14.1634 8.50415 14.1605 9.46484V11.2002C14.1604 12.1613 13.8619 12.8913 13.3734 13.3799C12.8848 13.8685 12.1547 14.1669 11.1937 14.167H4.80011C3.8389 14.167 3.10898 13.8681 2.62042 13.3789C2.1319 12.8896 1.83332 12.1582 1.83331 11.1934V9.4668C1.83331 8.50558 2.13179 7.77574 2.62042 7.28711C3.10905 6.79848 3.83889 6.5 4.80011 6.5Z"
      stroke="white"
    />
    <path
      d="M10.5866 3.71329L8.35331 1.47996C8.15998 1.28663 7.83998 1.28663 7.64664 1.47996L5.41331 3.71329C5.21998 3.90663 5.21998 4.22663 5.41331 4.41996C5.60664 4.61329 5.92664 4.61329 6.11998 4.41996L7.49998 3.03996V10.1666C7.49998 10.44 7.72664 10.6666 7.99998 10.6666C8.27331 10.6666 8.49998 10.44 8.49998 10.1666V3.03996L9.87998 4.41996C9.97998 4.51996 10.1066 4.56663 10.2333 4.56663C10.36 4.56663 10.4866 4.51996 10.5866 4.41996C10.7866 4.22663 10.7866 3.91329 10.5866 3.71329Z"
      fill="white"
    />
  </svg>
);

export const UploadButton: React.FC = () => {
  const dispatch = useDispatch();
  const enabled = useSelector(getUploadToProjectEnabled);
  const canUpload = useSelector(getCanUpload);
  const { isUploading, overallProgress, failedCount } = useSelector(getUploadStats);
  const completedInBackground = useSelector(getCompletedInBackground);

  if (!canUpload) {
    return null;
  }

  const progressPct = Math.round(overallProgress * 100);
  const isComplete = completedInBackground && !isUploading;
  const completedWithErrors = isComplete && failedCount > 0;

  // While a process is running or finished in the background the button stays
  // clickable (reopens the modal) regardless of the current selection.
  const active = isUploading || isComplete;

  const tooltipText = isUploading
    ? 'Uploading… click to view progress'
    : isComplete
      ? completedWithErrors
        ? 'Upload finished with errors — click for details'
        : 'Upload complete'
      : enabled
        ? 'Upload files to the selected project'
        : 'Select a single project to upload files';

  return (
    <>
      <Tooltip title={tooltipText} placement="bottom">
        <span className="al-uploader-button-wrap">
          <Button
            type="primary"
            color={isComplete ? (completedWithErrors ? 'orange' : 'green') : undefined}
            className={classNames('al-uploader-button', {
              'al-uploader-button_uploading': isUploading,
            })}
            onClick={() => dispatch(actions.modalOpened())}
            disabled={!enabled && !active}
          >
            {isUploading ? (
              <>
                <LoadingOutlined spin />
                <span>Uploading {progressPct}%</span>
              </>
            ) : isComplete ? (
              <>
                <CheckOutlined />
                <span>{completedWithErrors ? 'Completed with errors' : 'Complete'}</span>
              </>
            ) : (
              <>
                <UploadIcon16 />
                <span>Upload</span>
              </>
            )}
            {isUploading && (
              <span className="al-uploader-button__progress" style={{ width: `${progressPct}%` }} />
            )}
          </Button>
        </span>
      </Tooltip>
      <UploadModal />
    </>
  );
};
